import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  select,
} from '@ngrx/store';
import { pipe } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Code } from 'src/app/models/area-restaurant/code.model';
import { Restaurant } from 'src/app/models/area-restaurant/restaurant.model';
import { AllActionsUnion } from '../../../app.actions';
import {
  storeInitialData,
  storeNextPageRestaurants,
  storePrevPageRestaurants,
} from './area-restaurant-page.actions';

// State
export interface AreaRestaurantPageState {
  largeArea: Code;
  mediumArea: Code;
  smallArea: Code;
  currentGenre: Code;
  relatedArea: Code[];
  genresInArea: Code[];
  restaurants: Restaurant[];
  totalCount: number;
  minPage: number;
  maxPage: number;
}

const initialState: AreaRestaurantPageState = {
  largeArea: {
    name: '',
    code: '',
  },
  mediumArea: {
    name: '',
    code: '',
  },
  smallArea: {
    name: '',
    code: '',
  },
  currentGenre: {
    name: '',
    code: '',
  },
  restaurants: [],
  relatedArea: [],
  genresInArea: [],
  totalCount: 0,
  minPage: 1,
  maxPage: 1,
};

export const featureName = 'areaRestaurantPageFeature';

// Reducer
export function areaRestaurantPageReducer(
  state_: AreaRestaurantPageState | undefined,
  action: AllActionsUnion,
): AreaRestaurantPageState {
  const reducer_ = createReducer<AreaRestaurantPageState>(
    initialState,
    on(
      storeInitialData,
      (state, { payload }): AreaRestaurantPageState => {
        return {
          ...state,
          ...payload.data,
          minPage: payload.page,
          maxPage: payload.page,
        };
      },
    ),
    on(
      storePrevPageRestaurants,
      (state, { payload }): AreaRestaurantPageState => {
        return {
          ...state,
          restaurants: [...payload.restaurants, ...state.restaurants],
          minPage: payload.page,
        };
      },
    ),
    on(
      storeNextPageRestaurants,
      (state, { payload }): AreaRestaurantPageState => {
        return {
          ...state,
          restaurants: [...state.restaurants, ...payload.restaurants],
          maxPage: payload.page,
        };
      },
    ),
  );
  return reducer_(state_, action);
}

// Selectors
const getFeature = createFeatureSelector<AreaRestaurantPageState>(featureName);

export const getLargeArea = pipe(
  select(
    createSelector(
      getFeature,
      v => v.largeArea,
    ),
  ),
  distinctUntilChanged(),
);

export const getMediumArea = pipe(
  select(
    createSelector(
      getFeature,
      v => v.mediumArea,
    ),
  ),
  distinctUntilChanged(),
);

export const getSmallArea = pipe(
  select(
    createSelector(
      getFeature,
      v => v.smallArea,
    ),
  ),
  distinctUntilChanged(),
);

export const getCurrentGenre = pipe(
  select(
    createSelector(
      getFeature,
      v => v.currentGenre,
    ),
  ),
  distinctUntilChanged(),
);

export const getAreaRestaurant = pipe(
  select(
    createSelector(
      getFeature,
      v => {
        return {
          largeArea: v.largeArea,
          mediumArea: v.mediumArea,
          smallArea: v.smallArea,
          currentGenre: v.currentGenre,
          restaurants: v.restaurants,
          relatedAreas: v.relatedArea,
          genresInArea: v.genresInArea,
          totalCount: v.totalCount,
          minPage: v.minPage,
          maxPage: v.maxPage,
        };
      },
    ),
  ),
  distinctUntilChanged(),
);
